import { UserAgent, UserAgentOptions, Registerer, Inviter, Invitation, RegistererOptions, SessionState } from 'sip.js';

let userAgent = null;
let registerer = null;
let currentSession = null;
let autoAnswerIncoming = false;
let reconnectTimer = null;
let consultSession = null;
let pendingInvitation = null;

let initialCallAudio = null;
let consultCallAudio = null;
let sessionRemoteStreams = new WeakMap();
let sessionStateListeners = new WeakMap();
let conferenceCall = false;
let localMicTrack = null;

export async function registerSIP(username, password, sip_domain, onStatus, onIncomingCall, autoAnswer = false) {
  try {
    autoAnswerIncoming = autoAnswer;

    const uri = UserAgent.makeURI(`sip:${username}@${sip_domain}`);
    if (!uri) throw new Error('Invalid SIP URI');

    const transport = `wss://${sip_domain}`;

    const savedCtx = JSON.parse(localStorage.getItem('sip_register_context') || '{}');

    const instanceId = savedCtx.instance_id || crypto.randomUUID();
    const callId = savedCtx.call_id || crypto.randomUUID();
    const contactName = savedCtx.contact_name || username;
    const cseq = (savedCtx.cseq || 1) + 1;

    const userAgentOptions = {
      uri,
      authorizationUsername: username,
      authorizationPassword: password,
      contactName,
      viaHost: sip_domain,
      userAgentString: 'VoIPstudio SCV v1.0.0',
      autoStop: true,
      logBuiltinEnabled: true,
      transportOptions: { server: transport, keepAliveType: 'disabled' },
      delegate: {
        onConnect: () => {
          onStatus('SIP WebSocket connected');
          if (reconnectTimer) clearTimeout(reconnectTimer);
        },
        onDisconnect: () => {
          onStatus('Disconnected from SIP server.');
          scheduleReconnect(username, password, sip_domain, onStatus, onIncomingCall, autoAnswer);
        },
        onInvite: async (invitation) => {
          if (sessionStateListeners.get(invitation)) return;
          handleSessionState(invitation, onStatus);
          pendingInvitation = invitation;
          onStatus('Incoming call...');
          if (autoAnswerIncoming) {
            await acceptCall(invitation, onStatus, onIncomingCall);
          } else {
            onIncomingCall(invitation);
          }
        },
        onRefer: async (referral) => {
          onStatus(`Incoming transfer request to ${referral.target.toString()}`);
          if (autoAnswerIncoming) {
            await acceptCall(referral, onStatus, onIncomingCall);
          } else {
            onIncomingCall(referral, false);
          }
        },
      },
    };

    userAgent = new UserAgent(userAgentOptions);
    await userAgent.start();

    const registererOptions = {
      requestOptions: {
        extraHeaders: [
          `Call-ID: ${callId}`,
          `Contact: <sip:${contactName}@${sip_domain}>;+sip.instance="${instanceId}"`,
          `CSeq: ${cseq} REGISTER`,
        ],
      },
    };

    registerer = new Registerer(userAgent, registererOptions);

    registerer.stateChange.addListener((newState) => {
      if (newState === 'Registered') {
        onStatus('Registered with SIP server');
        const regContext = {
          call_id: callId,
          contact_name: contactName,
          cseq: cseq + 1,
          instance_id: instanceId,
          via_host: sip_domain,
          last_registered: Date.now(),
        };
        localStorage.setItem('sip_register_context', JSON.stringify(regContext));
      }
      if (newState === 'Unregistered') {
        onStatus('Unregistered from SIP server');
      }
    });

    await registerer.register();
  } catch (error) {
    onStatus(`Registration failed: ${error.message}`);
    console.error('SIP Registration Error:', error);
  }
}

function scheduleReconnect(username, password, sip_domain, onStatus, onIncomingCall, autoAnswer) {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => {
    onStatus('Attempting SIP reconnect...');
    const sipLoggedIn = localStorage.getItem('sipLoggedIn') === 'true';
    const storedUser = localStorage.getItem('sipUser');
    const storedPass = localStorage.getItem('sipPass');
    const storedDomain = localStorage.getItem('sipDomain');

    if (sipLoggedIn && storedUser && storedPass && storedDomain) {
      registerSIP(username, password, sip_domain, onStatus, onIncomingCall, autoAnswer);
    } else {
      onStatus('Reconnect skipped because user is logged out');
    }
  }, 5000);
}

export async function acceptCall(invitation, onStatus, onRemoteStream) {
  try {
    const invitationToAccept = invitation || pendingInvitation;

    if (!invitationToAccept) {
      throw new Error('No incoming invitation to accept');
    }

    await invitationToAccept.accept();

    if (!initialCallAudio) {
      initialCallAudio = new Audio();
      initialCallAudio.autoplay = true;
    }

    setupRemoteMedia(invitationToAccept, (stream) => {
      initialCallAudio.srcObject = stream;
      if (onRemoteStream) onRemoteStream(stream);
    });

    // handleSessionState(invitationToAccept, onStatus);
    // await ensureLocalMedia(invitationToAccept);

    onStatus('Call accepted');
    currentSession = invitationToAccept;
    pendingInvitation = null;
  } catch (error) {
    onStatus(`Failed to accept call: ${error.message}`);
    console.error('Accept Call Error:', error);
  }
}

async function ensureLocalMedia(session) {
  const pc = session.sessionDescriptionHandler?.peerConnection;
  if (!pc) return;

  try {
    const localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    const existingSenders = pc.getSenders().map((s) => s.track);
    localStream.getTracks().forEach((track) => {
      if (!existingSenders.includes(track)) {
        pc.addTrack(track, localStream);
      }
    });
  } catch (err) {
    console.warn('Microphone access denied or failed:', err);
  }
}

export async function makeCall(targetNumber, sip_domain, onStatus, onRemoteStream) {
  try {
    if (!userAgent || !registerer) throw new Error('Not registered. Please login first.');
    targetNumber = targetNumber.startsWith('#') ? targetNumber.replace(/^#/, '%23') : targetNumber;
    const target = UserAgent.makeURI(`sip:${targetNumber}@${sip_domain}`);
    if (!target) throw new Error('Invalid target SIP URI');

    const localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    const inviter = new Inviter(userAgent, target, createSessionOptions());

    if (!initialCallAudio) {
      initialCallAudio = new Audio();
      initialCallAudio.autoplay = true;
    }

    setupMediaHandlers(inviter, (stream) => {
      initialCallAudio.srcObject = stream;
      if (onRemoteStream) onRemoteStream(stream);
    });

    currentSession = inviter;
    handleSessionState(inviter, onStatus);

    await inviter.invite();
    onStatus(`Calling ${targetNumber}...`);
    return currentSession;
  } catch (error) {
    onStatus(`Call failed: ${error.message}`);
    console.error('Make Call Error:', error);
  }
}

function setupMediaHandlers(session, onRemoteStream) {
  session.stateChange.addListener(async (state) => {
    if (state === SessionState.Establishing) {
      // await ensureLocalMedia(session);
      setupRemoteMedia(session, onRemoteStream);
    }
  });
}

function createSessionOptions() {
  return {
    sessionDescriptionHandlerOptions: {
      constraints: { audio: true, video: false },
      peerConnectionOptions: {
        rtcConfiguration: {
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        },
      },
    },
    earlyMedia: true,
    requestDelegate: {
      onTrying: () => console.log('100 Trying'),
      onProgress: () => console.log('180 Ringing'),
      onAccept: () => console.log('200 OK'),
      onReject: (response) => console.log('Call rejected:', response.message.reasonPhrase),
    },
  };
}

function setupRemoteMedia(session, onRemoteStream) {
  const pc = session.sessionDescriptionHandler?.peerConnection;
  if (!pc) return;

  let remoteStream = sessionRemoteStreams.get(session);
  if (!remoteStream) {
    remoteStream = new MediaStream();
    sessionRemoteStreams.set(session, remoteStream);

    pc.addEventListener('track', (event) => {
      if (event.track.kind === 'audio') {
        if (!remoteStream.getTrackById(event.track.id)) {
          remoteStream.addTrack(event.track);
          onRemoteStream(remoteStream);
        }
      }
    });

    pc.getReceivers().forEach((receiver) => {
      const track = receiver.track;
      if (track && track.kind === 'audio' && !remoteStream.getTrackById(track.id)) {
        remoteStream.addTrack(track);
        onRemoteStream(remoteStream);
      }
    });
  }

  if (remoteStream.getAudioTracks().length > 0) {
    onRemoteStream(remoteStream);
  }
}

export function sendDTMF(tone) {
  try {
    if (!currentSession?.sessionDescriptionHandler) throw new Error('No active session');
    const pc = currentSession.sessionDescriptionHandler.peerConnection;
    const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
    if (!sender?.dtmf) throw new Error('DTMF not supported');
    sender.dtmf.insertDTMF(tone);
    console.log(`Sent DTMF tone: ${tone}`);
  } catch (error) {
    console.warn('DTMF Error:', error.message);
  }
}

export async function hangUp(onStatus, sessionType = 'current') {
  if (currentSession && consultSession && sessionType === 'both') {
    if (conferenceCall) {
      onStatus('Ending conference call...');
      await Promise.all([hangupSession(currentSession), hangupSession(consultSession)]);

      currentSession = null;
      consultSession = null;
      conferenceCall = false;
    } else {
      await completeTransfer(onStatus);
    }
    return;
  }

  let sessionToHangup = null;
  if (sessionType === 'current') {
    sessionToHangup = currentSession;
  } else if (sessionType === 'consult') {
    sessionToHangup = consultSession;
  }

  if (!sessionToHangup) {
    return;
  }

  try {
    onStatus('ending');

    if (sessionToHangup.state === SessionState.Established) {
      sessionToHangup.bye();
    } else if (sessionToHangup.state === SessionState.Establishing) {
      sessionToHangup.cancel();
    } else if (sessionToHangup.state === SessionState.Initial) {
      sessionToHangup.reject({
        statusCode: 603,
        reasonPhrase: 'Decline',
      });
    }

    onStatus('ended');
  } catch (err) {
    console.error('Hangup error:', err);
    onStatus('Call ended with error');
  } finally {
    if (sessionToHangup?.state === SessionState.Terminated) {
      if (sessionToHangup === currentSession) currentSession = null;
      if (sessionToHangup === consultSession) consultSession = null;
    }
  }
}

export function muteCall(onStatus) {
  if (!currentSession) return;
  try {
    const pc = currentSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) throw new Error('No active PeerConnection');

    pc.getSenders().forEach((sender) => {
      if (sender.track?.kind === 'audio') {
        sender.track.enabled = false;
      }
    });
    onStatus('Microphone muted');
  } catch (err) {
    console.error('Mute error:', err);
    onStatus('Failed to mute');
  }
}

export function unmuteCall(onStatus) {
  if (!currentSession) return;
  try {
    const pc = currentSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) throw new Error('No active PeerConnection');

    pc.getSenders().forEach((sender) => {
      if (sender.track?.kind === 'audio') {
        sender.track.enabled = true;
      }
    });
    onStatus('Microphone unmuted');
  } catch (err) {
    console.error('Unmute error:', err);
    onStatus('Failed to unmute');
  }
}

export function isMuted() {
  if (!currentSession) return false;
  const pc = currentSession.sessionDescriptionHandler?.peerConnection;
  if (!pc) return false;
  return pc.getSenders().some((sender) => sender.track?.kind === 'audio' && !sender.track.enabled);
}

export async function unregisterSip(onStatus) {
  try {
    if (currentSession) {
      try {
        if (currentSession.state === SessionState.Established) {
          await currentSession.bye();
        } else if (currentSession.state === SessionState.Establishing) {
          await currentSession.cancel();
        }
      } catch (err) {
        console.warn('Error ending currentSession:', err);
      }
      currentSession = null;
    }

    if (consultSession) {
      try {
        if (consultSession.state === SessionState.Established) {
          await consultSession.bye();
        } else if (consultSession.state === SessionState.Establishing) {
          await consultSession.cancel();
        }
      } catch (err) {
        console.warn('Error ending consultSession:', err);
      }
      consultSession = null;
    }
    // salesforce.ssl7.net
    await disconnectSipClient();

    try {
      if (localMicTrack) {
        localMicTrack.stop?.();
      }
    } catch (e) {
      console.warn('localMicTrack cleanup skipped:', e);
    }
    localMicTrack = null;

    sessionRemoteStreams = new WeakMap();

    if (initialCallAudio) {
      initialCallAudio.srcObject = null;
      initialCallAudio = null;
    }
    if (consultCallAudio) {
      consultCallAudio.srcObject = null;
      consultCallAudio = null;
    }

    onStatus('SIP client unregistered');
  } catch (error) {
    console.error('Unregister error:', error);
    onStatus('Error during unregister');
  } finally {
    registerer = null;
    userAgent = null;

    localStorage.setItem('sipLoggedIn', 'false');
    localStorage.removeItem('sipUser');
    localStorage.removeItem('sipPass');
    localStorage.removeItem('sipDomain');
    localStorage.removeItem('userToken');
    localStorage.removeItem('sip_registration');
    localStorage.removeItem('username');
    localStorage.removeItem('loginPayload');
  }
}

async function disconnectSipClient() {
  if (registerer) {
    try {
      await registerer.unregister();
    } catch (e) {
      console.warn('Unregister error:', e);
    }
    registerer = null;
  }

  if (userAgent) {
    try {
      await userAgent.stop();
    } catch (e) {
      console.warn('UserAgent stop error:', e);
    }

    try {
      userAgent.transport.dispose();
    } catch (e) {
      console.warn('Transport dispose error:', e);
    }
    userAgent = null;
  }
}

function handleSessionState(session, onStatus) {
  const listener = (newState) => {
    const sessionIdentifier = session.request?.callId;
    switch (newState) {
      case SessionState.Establishing:
        onStatus('incoming', sessionIdentifier);
        break;
      case SessionState.Established:
        onStatus('connected', sessionIdentifier);
        break;
      case SessionState.Terminating:
        onStatus('ending', sessionIdentifier);
        break;
      case SessionState.Terminated:
        onStatus('ended', sessionIdentifier);
        const storedListener = sessionStateListeners.get(session);
        if (storedListener) {
          session.stateChange.removeListener(storedListener);
          sessionStateListeners.delete(session);
        }
        if (session === currentSession) currentSession = null;
        if (session === consultSession) consultSession = null;
        break;
    }
  };

  sessionStateListeners.set(session, listener);
  session.stateChange.addListener(listener);
}

export function blindTransfer(targetNumber, sip_domain, onStatus) {
  if (!currentSession) {
    onStatus('No active session to transfer');
    return;
  }

  try {
    const transferTarget = UserAgent.makeURI(`sip:${targetNumber}@${sip_domain}`);
    if (!transferTarget) throw new Error('Invalid transfer target URI');

    const callerE164 = currentSession?.request?.headers?.['X-From-E164']?.[0]?.raw || '';

    currentSession.refer(transferTarget, {
      extraHeaders: callerE164 ? [`X-From-E164: ${callerE164}`] : [],
    });

    onStatus(`Blind transferring call to ${targetNumber}`);
  } catch (error) {
    onStatus(`Transfer failed: ${error.message}`);
    console.error('Blind Transfer Error:', error);
  }
}

export async function holdCall(session, onStatus) {
  if (!session) {
    onStatus('No active session to hold');
    return;
  }

  try {
    await session.invite({
      requestDelegate: {
        onAccept: () => onStatus(`Call on hold with ${session.remoteIdentity.uri}`),
      },
      sessionDescriptionHandlerModifiers: [
        (description) => {
          description.sdp = description.sdp.replace(/a=(sendrecv|recvonly|inactive)/g, 'a=sendonly');
          return Promise.resolve(description);
        },
      ],
    });
  } catch (err) {
    console.error('Hold failed:', err);
    onStatus('Failed to hold call');
  }
}

export async function resumeCall(session, onStatus) {
  if (!session) {
    onStatus('No active session to resume');
    return;
  }

  try {
    await session.invite({
      requestDelegate: {
        onAccept: () => onStatus(`Call resumed with ${session.remoteIdentity.uri}`),
      },
      sessionDescriptionHandlerModifiers: [
        (description) => {
          description.sdp = description.sdp.replace(/a=(sendonly|recvonly|inactive)/g, 'a=sendrecv');
          return Promise.resolve(description);
        },
      ],
    });
  } catch (err) {
    console.error('Resume failed:', err);
    onStatus('Failed to resume call');
  }
}

export async function consultTransfer(consultTarget, sip_domain, onStatus) {
  if (!currentSession) {
    onStatus('No active session to transfer from');
    return;
  }

  try {
    await holdCall(currentSession, onStatus);
    onStatus('Put current call on hold, starting consult...');

    const consultURI = UserAgent.makeURI(`sip:${consultTarget}@${sip_domain}`);
    if (!consultURI) throw new Error('Invalid consult target URI');

    const inviter = new Inviter(userAgent, consultURI, createSessionOptions());

    if (!consultCallAudio) {
      consultCallAudio = new Audio();
      consultCallAudio.autoplay = true;
    }

    setupMediaHandlers(inviter, (stream) => {
      consultCallAudio.srcObject = stream;
      console.log('Consult call remote stream received');
    });

    consultSession = inviter;
    handleSessionState(consultSession, onStatus);

    await inviter.invite();
    onStatus(`Consulting with ${consultTarget}...`);
  } catch (error) {
    onStatus(`Consult transfer failed: ${error.message}`);
    console.error('Consult Transfer Error:', error);
  }
}

async function hangupSession(session) {
  if (!session) return;

  try {
    if (session.state === SessionState.Established) {
      await session.bye();
    } else if (session.state === SessionState.Establishing) {
      await session.cancel();
    } else if (session.state === SessionState.Initial) {
      session.reject({
        statusCode: 603,
        reasonPhrase: 'Decline',
      });
    }
  } catch (err) {
    console.warn('Error hanging up session:', err);
  }
}

export async function completeTransfer(onStatus) {
  if (!consultSession || !currentSession) {
    onStatus('No active consult session to complete transfer');
    return;
  }

  const customerURI = currentSession.remoteIdentity.uri.toString();
  const agentURI = consultSession.remoteIdentity.uri.toString();

  try {
    onStatus('Completing warm transfer...');

    // await consultSession.refer(currentSession.remoteIdentity.uri);
    await currentSession.refer(consultSession);
    console.log(`Warm transfer initiated: ${agentURI} → ${customerURI}`);

    await Promise.all([hangupSession(currentSession), hangupSession(consultSession)]);

    currentSession = null;
    consultSession = null;

    onStatus('Warm transfer completed. Call handed over.');
  } catch (err) {
    console.error('Complete Transfer Error:', err);
    onStatus(`Transfer failed: ${err.message}. Both calls remain active.`);
  }
}

export async function swapCalls(activeSession, holdSession, onStatus) {
  if (!activeSession && !holdSession) {
    activeSession = currentSession;
    holdSession = consultSession;
  }

  if (!activeSession || !holdSession) {
    console.log('Swap calls - Current session:', !!currentSession, 'Consult session:', !!consultSession);
    onStatus('Both sessions are required to swap. Need current and consult sessions.');
    return;
  }

  try {
    await resumeCall(activeSession, onStatus);
    await holdCall(holdSession, onStatus);

    if (activeSession === currentSession) {
      if (!initialCallAudio) {
        initialCallAudio = new Audio();
        initialCallAudio.autoplay = true;
      }
      setupRemoteMedia(currentSession, (stream) => {
        initialCallAudio.srcObject = stream;
        initialCallAudio.muted = false;
      });
      if (consultCallAudio) consultCallAudio.muted = true;
      // await ensureLocalMedia(currentSession);
    } else if (activeSession === consultSession) {
      if (!consultCallAudio) {
        consultCallAudio = new Audio();
        consultCallAudio.autoplay = true;
      }
      setupRemoteMedia(consultSession, (stream) => {
        consultCallAudio.srcObject = stream;
        consultCallAudio.muted = false;
      });
      if (initialCallAudio) initialCallAudio.muted = true;
      // await ensureLocalMedia(consultSession);
    }

    onStatus(
      `Swapped calls: ${activeSession.remoteIdentity.uri} is now active, ${holdSession.remoteIdentity.uri} is on hold`,
    );
  } catch (err) {
    console.error('Swap calls error:', err);
    onStatus('Failed to swap calls');
  }
}

export async function conference(onStatus, audioElement) {
  if (!currentSession || !consultSession) {
    console.log('Conference - Current session:', !!currentSession, 'Consult session:', !!consultSession);
    onStatus('Both current and consult sessions are required for conference. Start a consult call first.');
    return;
  }

  try {
    onStatus('Starting conference call...');

    await resumeCall(currentSession, onStatus);
    await resumeCall(consultSession, onStatus);
    conferenceCall = true;

    const mixedStream = new MediaStream();

    [currentSession, consultSession].forEach((session) => {
      const pc = session.sessionDescriptionHandler?.peerConnection;
      if (!pc) return;

      pc.getReceivers().forEach((receiver) => {
        if (receiver.track && receiver.track.kind === 'audio') {
          mixedStream.addTrack(receiver.track);
        }
      });

      pc.addEventListener('track', (event) => {
        if (event.track.kind === 'audio') {
          mixedStream.addTrack(event.track);
          if (audioElement) {
            audioElement.srcObject = mixedStream;
          }
        }
      });
    });

    const conferenceAudio = audioElement || initialCallAudio;
    if (conferenceAudio) {
      conferenceAudio.srcObject = mixedStream;
      conferenceAudio.play();
    }

    // await ensureLocalMedia(currentSession);
    // await ensureLocalMedia(consultSession);

    onStatus('Conference started (three-way call active)');
  } catch (err) {
    console.error('Conference error:', err);
    onStatus('Failed to establish conference');
  }
}

export function getPendingInvitation() {
  return pendingInvitation;
}

export function getCurrentSession() {
  return currentSession;
}

export function getConsultSession() {
  return consultSession;
}

export function hasMultipleSessions() {
  return !!(currentSession && consultSession);
}

export function getSessionInfo() {
  return {
    hasCurrentSession: !!currentSession,
    hasConsultSession: !!consultSession,
    hasPendingInvitation: !!pendingInvitation,
    currentSessionState: currentSession?.state || 'none',
    consultSessionState: consultSession?.state || 'none',
  };
}
